import { z } from 'zod';

import { PAGINATION } from '../constants/index.js';

import {
  CurrencyCodeSchema,
  EmailSchema,
  IsoDateTimeSchema,
  StoredEmailSchema,
  UuidSchema,
  BooleanQueryParamSchema,
} from './common.js';
import { MobileCountrySchema, MobileNumberSchema } from './customers.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Lead shapes (Phase 4 - lead CRM core).
 *
 * Layered the same way as customers:
 *   - LeadListItem   - light row for grids + dashboard widgets
 *   - LeadDetail     - the lead-edit page (products + recent stage history +
 *                      upcoming follow-ups joined)
 *   - Create/Update  - agent/admin write paths
 *   - TransitionStage / AddFollowUp / CompleteFollowUp - explicit verb DTOs
 *     so the service can enforce the side-effects (history row, denorm
 *     update, bucket flip) atomically.
 *
 * Stage progression is driven by `query_statuses` (the seeded master); the
 * application reads the row's `bucket` to mirror it into `leads.current_bucket`
 * on every transition. We don't expose an enum for the stage CODE because
 * ops can add stages without a code change.
 */

// ---------------------------------------------------------------------------
// Enums (mirror prisma/schema.prisma)
// ---------------------------------------------------------------------------

export const LeadPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
export type LeadPriority = z.infer<typeof LeadPrioritySchema>;

export const LeadCategorySchema = z.enum(['DOMESTIC', 'INTERNATIONAL']);
export type LeadCategory = z.infer<typeof LeadCategorySchema>;

export const LeadBucketSchema = z.enum(['OPEN', 'WON', 'LOST']);
export type LeadBucket = z.infer<typeof LeadBucketSchema>;

/**
 * "How soon customer will book?" (legacy parity, ADR-0035). Buckets the
 * sales cycle - used in the lead form + reports.
 */
export const BookingTimelineSchema = z.enum([
  'WITHIN_7_DAYS',
  'EIGHT_TO_15_DAYS',
  'SIXTEEN_TO_30_DAYS',
  'ONE_TO_THREE_MONTHS',
  'MORE_THAN_THREE_MONTHS',
]);
export type BookingTimeline = z.infer<typeof BookingTimelineSchema>;

/**
 * Hotel meal-plan code. `CPAI` carried over from the legacy data; AP, MAP,
 * CP, EP, AI are the standard travel-industry codes operators use when
 * negotiating with TBO / hotel suppliers (per ADR-0035).
 */
export const MealOptionSchema = z.enum(['CPAI', 'AP', 'MAP', 'CP', 'EP', 'AI']);
export type MealOption = z.infer<typeof MealOptionSchema>;

export const LeadFollowUpKindSchema = z.enum(['CALL', 'WHATSAPP', 'EMAIL', 'IN_PERSON', 'OTHER']);
export type LeadFollowUpKind = z.infer<typeof LeadFollowUpKindSchema>;

export const LeadFollowUpResultSchema = z.enum([
  'SCHEDULED',
  'CONNECTED',
  'NO_RESPONSE',
  'DELAYED',
  'COMPLETED',
  'CANCELED',
]);
export type LeadFollowUpResult = z.infer<typeof LeadFollowUpResultSchema>;

// ---------------------------------------------------------------------------
// Reference sub-schemas
// ---------------------------------------------------------------------------

const NamedRefSchema = z.object({ id: UuidSchema, name: z.string() });
const CodedRefSchema = z.object({ id: UuidSchema, code: z.string(), name: z.string() });
const StatusRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  bucket: LeadBucketSchema.nullable(),
  /** Editable indicator color from the master (ADR-0032 §18). NULL → infer. */
  color: z.string().nullable(),
});
const SubStatusRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  queryStatusId: UuidSchema,
  /** Editable indicator color; NULL inherits from parent status. */
  color: z.string().nullable(),
});
const UserRefSchema = z.object({ id: UuidSchema, fullName: z.string() });
const CustomerRefSchema = z.object({
  id: UuidSchema,
  kind: z.enum(['INDIVIDUAL', 'CORPORATE']),
  displayName: z.string(),
  /** Optional user-facing itinerary name override. */
  primaryName: z.string().nullable().optional(),
  email: StoredEmailSchema.nullable(),
  /// 2026-06-25. Persistent secondary email surfaced on the lead form's
  /// "Alternate email" input so it hydrates on edit. Promoted to the
  /// primary `email` via the "Mark as primary" checkbox on save.
  alternateEmail: StoredEmailSchema.nullable().optional(),
  mobileNumber: z.string().nullable(),
  /**
   * Alternate phone number on the customer (legacy "Alternate Contact #"
   * field per ADR-0035). Surfaces in the lead-edit form so operators can
   * keep a secondary number on file.
   */
  altPhone: z.string().nullable().optional(),
  /** CORPORATE customers' registered name. NULL for INDIVIDUAL. */
  companyName: z.string().nullable().optional(),
});

// ---------------------------------------------------------------------------
// Lead products
// ---------------------------------------------------------------------------

export const LeadProductSchema = z.object({
  id: UuidSchema,
  destination: z.string(),
  productType: z.string(),
  notes: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type LeadProduct = z.infer<typeof LeadProductSchema>;

export const LeadProductInputSchema = z.object({
  destination: z.string().min(1).max(120),
  /** Free-text for Phase 4 -- promoted to FK when packages master lands. */
  productType: z.string().min(1).max(60),
  notes: z.string().max(2000).nullable().optional(),
});
export type LeadProductInput = z.infer<typeof LeadProductInputSchema>;

// ---------------------------------------------------------------------------
// Stage history
// ---------------------------------------------------------------------------

export const LeadStageHistoryEntrySchema = z.object({
  id: UuidSchema,
  fromStatus: StatusRefSchema.nullable(),
  toStatus: StatusRefSchema,
  fromSubStatus: SubStatusRefSchema.nullable(),
  toSubStatus: SubStatusRefSchema.nullable(),
  note: z.string().nullable(),
  changedBy: UserRefSchema,
  changedAt: IsoDateTimeSchema,
});
export type LeadStageHistoryEntry = z.infer<typeof LeadStageHistoryEntrySchema>;

export const TransitionStageRequestSchema = z.object({
  toStatusId: UuidSchema.describe('Target query_statuses.id'),
  /**
   * Optional sub-status drilldown. Must belong to `toStatusId` (server
   * enforces). Pass `null` (or omit) when the target status has no sub-status
   * entries selected.
   */
  toSubStatusId: UuidSchema.nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
  /** Required when the target status is in the LOST bucket. */
  lostReasonId: UuidSchema.nullable().optional(),
  /**
   * Win-conversion details - required when the target status is in the WON
   * bucket (the server enforces). Carried on the direct-transition path used
   * by admins; agents/leads send the same data via the approval request.
   */
  convertedItineraryId: UuidSchema.optional(),
  /** Published ItineraryRevision the win is pinned to - required alongside convertedItineraryId when the target is WON. */
  convertedRevisionId: UuidSchema.optional(),
  /**
   * 2026-08-26. Manual (external) close - ADMIN only. When true the deal was
   * confirmed OUTSIDE the system, so no itinerary version is pinned: the
   * operator supplies `saleAmount`, `ttvAmount`, `conversionDate` (closing
   * date) and `expectedPaymentDate` (payment date) by hand. The server relaxes
   * the published-version requirement and skips every itinerary side-effect
   * (accept, sibling-cancel, payment-link cleanup, TTV re-sync). Reports stay
   * correct because they read `ttvAmount`/`saleAmount`/`closedAt` off the lead.
   */
  manualClose: z.boolean().optional(),
  /**
   * Pre-tax TTV entered by hand on a manual close (on a normal win this is
   * re-synced from the converted itinerary's taxable base). Ignored unless
   * `manualClose` is set.
   */
  ttvAmount: z.number().nonnegative().optional(),
  saleAmount: z.number().nonnegative().optional(),
  /**
   * First-installment / non-refundable deposit actually collected up front,
   * captured by hand on a manual WON (on a payment-driven win the paid first
   * instalment records this instead). Recorded against the full sale value;
   * informational. Rupee amount; ignored unless the move is a WON.
   */
  wonDepositAmount: z.number().nonnegative().optional(),
  conversionDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /**
   * 2026-07-13. Expected payment date. Required when the target sub-status
   * is `hot` and the lead does not already carry one - the "Hot" sub-status
   * marks a warm-to-book lead and the operator must record when they expect
   * money to land. The value is written onto the lead as part of the same
   * transaction as the stage move so the two never drift.
   */
  expectedPaymentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  /**
   * 2026-08-06. Budget captured inline when marking a lead LOST with a reason
   * whose `requiresBudget` flag is set (e.g. "Low budget") and the lead has no
   * budget yet. Written onto the lead in the same transaction as the stage
   * move, mirroring `expectedPaymentDate` for Hot.
   */
  budgetAmount: z.number().nonnegative().nullable().optional(),
});
export type TransitionStageRequest = z.infer<typeof TransitionStageRequestSchema>;

// ---------------------------------------------------------------------------
// CallerDesk click-to-call
// ---------------------------------------------------------------------------

/**
 * Which customer number to bridge: the lead's snapshot mobile (PRIMARY) or the
 * customer's alternate contact (ALTERNATE). An enum - never a raw number - so
 * the server only ever dials numbers already on record for the lead.
 */
export const CallLeadTargetSchema = z.enum(['PRIMARY', 'ALTERNATE']);
export type CallLeadTarget = z.infer<typeof CallLeadTargetSchema>;

export const CallLeadRequestSchema = z.object({
  target: CallLeadTargetSchema.optional().default('PRIMARY'),
});
export type CallLeadRequest = z.infer<typeof CallLeadRequestSchema>;

// ---------------------------------------------------------------------------
// ADR-0054: out-of-band approval for LOST transitions.
// ---------------------------------------------------------------------------

export const LeadStatusChangeRequestStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type LeadStatusChangeRequestStatusValue = z.infer<
  typeof LeadStatusChangeRequestStatusSchema
>;

/** Shared approval inbox kinds (LOST transitions + destination edits + WON transitions). */
export const LeadApprovalRequestKindSchema = z.enum([
  'LOST_TRANSITION',
  'DESTINATION_CHANGE',
  'WON_TRANSITION',
  'STATUS_CHANGE',
]);
export type LeadApprovalRequestKind = z.infer<typeof LeadApprovalRequestKindSchema>;

/**
 * Win-conversion details captured when a lead is marked Won. All three are
 * required at the point of winning (whether the actor wins directly or
 * submits for approval). `saleAmount` is a non-negative rupee amount;
 * `conversionDate` is a calendar date (YYYY-MM-DD).
 */
export const WonConversionInputSchema = z.object({
  convertedItineraryId: UuidSchema,
  convertedRevisionId: UuidSchema,
  saleAmount: z.number().nonnegative(),
  conversionDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
});
export type WonConversionInput = z.infer<typeof WonConversionInputSchema>;

/** Sales agents submit this when they want to move a lead to LOST or change destination on edit. */
export const CreateStatusChangeRequestRequestSchema = z
  .object({
    kind: LeadApprovalRequestKindSchema.default('LOST_TRANSITION'),
    toStatusId: UuidSchema.optional(),
    toSubStatusId: UuidSchema.nullable().optional(),
    /** Required by the server when the picked target is the LOST bucket. */
    lostReasonId: UuidSchema.nullable().optional(),
    /** Required when `kind` is DESTINATION_CHANGE (maps to lead.destinationCityId). */
    toDestinationId: UuidSchema.optional(),
    /** Win-conversion details - required when `kind` is WON_TRANSITION. */
    convertedItineraryId: UuidSchema.optional(),
    convertedRevisionId: UuidSchema.optional(),
    saleAmount: z.number().nonnegative().optional(),
    conversionDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    requestorNote: z.string().max(2000).nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'LOST_TRANSITION') {
      if (!value.toStatusId) {
        ctx.addIssue({
          code: 'custom',
          message: 'toStatusId is required for LOST approval requests',
          path: ['toStatusId'],
        });
      }
      if (!value.requestorNote || value.requestorNote.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'A remark is required when marking a lead as lost.',
          path: ['requestorNote'],
        });
      }
      return;
    }
    if (value.kind === 'STATUS_CHANGE') {
      if (!value.toStatusId) {
        ctx.addIssue({
          code: 'custom',
          message: 'toStatusId is required for status-change approval requests',
          path: ['toStatusId'],
        });
      }
      return;
    }
    if (value.kind === 'WON_TRANSITION') {
      if (!value.toStatusId) {
        ctx.addIssue({
          code: 'custom',
          message: 'toStatusId is required for WON approval requests',
          path: ['toStatusId'],
        });
      }
      if (!value.convertedItineraryId) {
        ctx.addIssue({
          code: 'custom',
          message: 'Itinerary is required to mark a lead as Won',
          path: ['convertedItineraryId'],
        });
      }
      if (!value.convertedRevisionId) {
        ctx.addIssue({
          code: 'custom',
          message: 'A published version is required to mark a lead as Won',
          path: ['convertedRevisionId'],
        });
      }
      if (value.saleAmount === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'Sale amount is required to mark a lead as Won',
          path: ['saleAmount'],
        });
      }
      if (!value.conversionDate) {
        ctx.addIssue({
          code: 'custom',
          message: 'Conversion date is required to mark a lead as Won',
          path: ['conversionDate'],
        });
      }
      return;
    }
    if (!value.toDestinationId) {
      ctx.addIssue({
        code: 'custom',
        message: 'toDestinationId is required for destination change requests',
        path: ['toDestinationId'],
      });
    }
  });
export type CreateStatusChangeRequestRequest = z.input<
  typeof CreateStatusChangeRequestRequestSchema
>;

/** Admin approve / reject payload. */
export const ReviewStatusChangeRequestRequestSchema = z.object({
  reviewerNote: z.string().max(2000).nullable().optional(),
  /**
   * Hand the lead to this agent in the same step as the approval. Only
   * honoured when APPROVING a DESTINATION_CHANGE request (rejected on any other
   * kind, ignored on a rejection): a new destination often belongs to a
   * different agent's coverage. Must be one of the request's assignee options -
   * see `DestinationChangeAssigneeOptionsResponseSchema`.
   */
  assignedAgentId: UuidSchema.optional(),
});
export type ReviewStatusChangeRequestRequest = z.infer<
  typeof ReviewStatusChangeRequestRequestSchema
>;

/**
 * Who the reviewer may hand the lead to while approving ONE pending
 * DESTINATION_CHANGE request.
 *
 * Wider than `/users/assignable-agents` for a SALES_LEAD, on purpose and only
 * here: self + direct reports PLUS every other active SALES_LEAD, because the
 * new destination may belong to another team and the hand-off goes to that
 * team's lead. ADMIN gets every active sales user, as everywhere else.
 * `coversDestination` is the ADR-0057 sales-coverage check run against the
 * REQUESTED destination, so the picker can say who can actually take the lead.
 */
export const DestinationChangeAssigneeOptionsResponseSchema = z.object({
  currentAgentId: UuidSchema.nullable(),
  data: z.array(
    z.object({
      id: UuidSchema,
      fullName: z.string(),
      role: z.enum(['SALES_LEAD', 'SALES_AGENT']),
      branchId: UuidSchema.nullable(),
      coversDestination: z.boolean(),
    }),
  ),
});
export type DestinationChangeAssigneeOptionsResponse = z.infer<
  typeof DestinationChangeAssigneeOptionsResponseSchema
>;

/** Admin-only bulk approve payload. Request (LeadStatusChangeRequest) ids to approve. */
export const BulkApproveStatusChangeRequestsRequestSchema = z.object({
  ids: z.array(UuidSchema).min(1).max(200),
});
export type BulkApproveStatusChangeRequestsRequest = z.infer<
  typeof BulkApproveStatusChangeRequestsRequestSchema
>;
export const BulkApproveStatusChangeRequestsResponseSchema = z.object({
  approved: z.number().int().nonnegative(),
  failed: z.array(z.object({ id: z.string(), reason: z.string() })),
});
export type BulkApproveStatusChangeRequestsResponse = z.infer<
  typeof BulkApproveStatusChangeRequestsResponseSchema
>;

export const LeadStatusChangeRequestSchema = z.object({
  id: UuidSchema,
  kind: LeadApprovalRequestKindSchema,
  status: LeadStatusChangeRequestStatusSchema,
  fromStatus: StatusRefSchema,
  fromSubStatus: SubStatusRefSchema.nullable(),
  toStatus: StatusRefSchema,
  toSubStatus: SubStatusRefSchema.nullable(),
  lostReason: NamedRefSchema.nullable(),
  fromDestination: NamedRefSchema.nullable(),
  toDestination: NamedRefSchema.nullable(),
  /// WON_TRANSITION payload (null for other kinds).
  saleAmount: z.number().nullable(),
  conversionDate: z.string().nullable(),
  convertedItineraryId: UuidSchema.nullable(),
  convertedRevisionId: UuidSchema.nullable(),
  requestedBy: UserRefSchema,
  /// When set, this SALES_LEAD reviews the request. Null = admin pool.
  assignedReviewer: UserRefSchema.nullable(),
  requestedAt: IsoDateTimeSchema,
  requestorNote: z.string().nullable(),
  reviewedBy: UserRefSchema.nullable(),
  reviewedAt: IsoDateTimeSchema.nullable(),
  reviewerNote: z.string().nullable(),
  /** Last sales-agent nudge to the approver; null until first nudge. */
  lastNudgedAt: IsoDateTimeSchema.nullable(),
});
export type LeadStatusChangeRequestT = z.infer<typeof LeadStatusChangeRequestSchema>;

/** Response from POST …/status-change-requests/:requestId/nudge. */
export const NudgeStatusChangeRequestResponseSchema = z.object({
  id: UuidSchema,
  lastNudgedAt: IsoDateTimeSchema,
});
export type NudgeStatusChangeRequestResponse = z.infer<
  typeof NudgeStatusChangeRequestResponseSchema
>;

/** Inbox row for the centralized lead approvals screen (ADR-0054 hierarchy). */
export const LeadStatusChangeRequestInboxItemSchema = LeadStatusChangeRequestSchema.extend({
  leadId: UuidSchema,
  leadReference: z.string(),
  customerName: z.string().nullable(),
  /** When the lead itself was generated (lead.createdAt) - shown so reviewers see lead age. */
  leadCreatedAt: IsoDateTimeSchema,
});
export type LeadStatusChangeRequestInboxItem = z.infer<
  typeof LeadStatusChangeRequestInboxItemSchema
>;

export const LeadStatusChangeRequestListQuerySchema = PaginationQuerySchema.extend({
  // Optional so the approvals page can show EVERY request (all statuses) as a
  // complete record - omit to return pending + approved + rejected together.
  status: LeadStatusChangeRequestStatusSchema.optional(),
  kind: LeadApprovalRequestKindSchema.optional(),
  /**
   * Narrow the inbox to one team member's submissions - "show me everything
   * <person> has ever raised". Always ANDed with the caller's reviewer scope
   * server-side, so a SALES_LEAD passing an arbitrary id still only sees rows
   * assigned to them; it can narrow their view, never widen it.
   */
  requestedById: UuidSchema.optional(),
  /**
   * Narrow to requests actioned by one reviewer - "what has <person>
   * approved or rejected". PENDING rows have no reviewer yet, so setting
   * this naturally excludes them. Same scope guarantee as `requestedById`.
   */
  reviewedById: UuidSchema.optional(),
});

/**
 * Option for the approvals inbox "Approved by" filter.
 *
 * Derived from DISTINCT `reviewedById` on the approvals table rather than
 * from a user-list endpoint. Reviewers are ADMIN *or* SALES_LEAD, and
 * `/users/assignable-agents` (which backs the "Requested by" filter) returns
 * SALES_LEAD + SALES_AGENT - so it would miss every admin reviewer while
 * listing agents who can never review. Reading the table gives exactly the
 * people who have actually actioned a row the caller can see.
 */
export const LeadApprovalReviewerOptionSchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
});
export type LeadApprovalReviewerOption = z.infer<typeof LeadApprovalReviewerOptionSchema>;

export const LeadApprovalReviewerListResponseSchema = z.array(LeadApprovalReviewerOptionSchema);
export type LeadApprovalReviewerListResponse = z.infer<
  typeof LeadApprovalReviewerListResponseSchema
>;
export type LeadStatusChangeRequestListQuery = z.infer<
  typeof LeadStatusChangeRequestListQuerySchema
>;

export const LeadStatusChangeRequestListResponseSchema = paginatedResponseSchema(
  LeadStatusChangeRequestInboxItemSchema,
);
export type LeadStatusChangeRequestListResponse = z.infer<
  typeof LeadStatusChangeRequestListResponseSchema
>;

export const LeadStatusChangeRequestPendingCountSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type LeadStatusChangeRequestPendingCount = z.infer<
  typeof LeadStatusChangeRequestPendingCountSchema
>;

/**
 * Optional scope for the pending-count endpoint.
 *
 * 2026-07-23. The Lead inbox owner filter drives the Daily Tracker's
 * "Pending Approvals" tile, so the tile can answer "how many approvals are
 * waiting on <owner>'s work" instead of only the caller-wide queue depth.
 * `requestedById` matches the approvals-inbox filter of the same name, so the
 * tile count and the page it drills into are computed from one predicate.
 * Same scope guarantee: it is ANDed with the caller's reviewer scope
 * server-side, so it can only narrow the view, never widen it.
 */
export const LeadStatusChangeRequestPendingCountQuerySchema = z.object({
  requestedById: UuidSchema.optional(),
});
export type LeadStatusChangeRequestPendingCountQuery = z.infer<
  typeof LeadStatusChangeRequestPendingCountQuerySchema
>;

/** A single field the lead is missing to be promoted to ACTIVE. ADR-0054. */
export const MissingFieldSchema = z.object({
  field: z.string(),
  label: z.string(),
});
export type MissingField = z.infer<typeof MissingFieldSchema>;

// ---------------------------------------------------------------------------
// Follow-ups
// ---------------------------------------------------------------------------

export const LeadFollowUpSchema = z.object({
  id: UuidSchema,
  scheduledAt: IsoDateTimeSchema,
  kind: LeadFollowUpKindSchema,
  result: LeadFollowUpResultSchema,
  callStatus: CodedRefSchema.nullable(),
  callSubStatus: CodedRefSchema.nullable(),
  agent: UserRefSchema,
  notes: z.string().nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  nextFollowUpAt: IsoDateTimeSchema.nullable(),
  /** Who created this follow-up: `SYSTEM` = the automated NQ follow-up cron,
   *  `AGENT` = a human. Drives the "Source" column in the follow-up history. */
  source: z.enum(['AGENT', 'SYSTEM']),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type LeadFollowUp = z.infer<typeof LeadFollowUpSchema>;

export const ScheduleFollowUpRequestSchema = z.object({
  scheduledAt: IsoDateTimeSchema,
  kind: LeadFollowUpKindSchema.default('CALL'),
  agentId: UuidSchema.describe('Agent assigned to make this call. Defaults to lead owner.'),
  notes: z.string().max(2000).nullable().optional(),
});
export type ScheduleFollowUpRequest = z.infer<typeof ScheduleFollowUpRequestSchema>;

export const CompleteFollowUpRequestSchema = z
  .object({
    result: LeadFollowUpResultSchema,
    callStatusId: UuidSchema.nullable().optional(),
    /** Optional child sub-status (under the picked parent). The server
     *  validates that this row belongs to the same `callStatusId`. */
    callSubStatusId: UuidSchema.nullable().optional(),
    /** ADR-0051. FK to `followup_reasons`. The server requires this when
     *  the picked sub-status has `isFollowUpReason = true` and rejects it
     *  when the flag is false (no stray reason data on outcomes that
     *  don't want one). */
    followupReasonId: UuidSchema.nullable().optional(),
    /** ADR-0053. FK to `lost_reasons`. The server requires this when the
     *  picked sub-status has `isLostReason = true` and rejects it
     *  otherwise. When set + valid, the server ALSO transitions the lead
     *  to the seeded `lost` QueryStatus in the same transaction (so the
     *  agent doesn't have to make a second edit). */
    lostReasonId: UuidSchema.nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    /** When set, the service inserts a new SCHEDULED follow-up for this time
     *  and updates the parent lead's `nextFollowUpAt` denorm. When NULL +
     *  the picked sub-status has `isAutoFollowup = true`, the server
     *  computes the next attempt via `auto-followup.helper` subject to the
     *  SOP caps (21 lifetime / 3 per day). ADR-0051. */
    nextFollowUpAt: IsoDateTimeSchema.nullable().optional(),
  })
  .refine((v) => v.result !== 'SCHEDULED', {
    message: 'Completion result cannot be SCHEDULED. Use the schedule endpoint instead.',
    path: ['result'],
  })
  .refine((v) => !v.lostReasonId || (typeof v.notes === 'string' && v.notes.trim().length > 0), {
    message: 'A remark is required when marking a lead as lost.',
    path: ['notes'],
  });
export type CompleteFollowUpRequest = z.infer<typeof CompleteFollowUpRequestSchema>;

export {
  LARGE_LEAD_ADULTS_THRESHOLD,
  isLargeLeadQueryByAdults,
} from '../leads/large-lead-query.js';

export {
  LEAD_STATUS_RANK,
  ACTIVE_SUB_STATUS_RANK,
  leadStatusRank,
  activeSubStatusRank,
  isBackwardActiveSubStatusMove,
} from '../leads/stage-ladder.js';

export {
  scoreLeadIntent,
  LEAD_INTENT_BUDGET_THRESHOLD,
  type LeadIntentSignals,
  type AutoLeadIntent,
} from '../leads/lead-intent-score.js';

// ---------------------------------------------------------------------------
// Lead list + detail
// ---------------------------------------------------------------------------

export const LeadListItemSchema = z.object({
  id: UuidSchema,
  /** Numeric upstream id (legacy `tbl_CRMLead.LeadId`); null for rows born in the new system. */
  legacyId: z.number().int().nullable(),
  reference: z.string(),
  title: z.string(),
  customer: CustomerRefSchema,
  currentStatus: StatusRefSchema,
  /** Finer-grained sub-status under `currentStatus`. NULL when the status
   *  has no sub-statuses, or the operator hasn't picked one yet. */
  currentSubStatus: SubStatusRefSchema.nullable(),
  /** Reason the lead was written off; set only for LOST leads. */
  lostReason: NamedRefSchema.nullable(),
  /** Note from the lead's most recent follow-up that carried a remark. */
  lastFollowUpNote: z.string().nullable(),
  currentBucket: LeadBucketSchema,
  priority: LeadPrioritySchema.nullable(),
  leadCategory: LeadCategorySchema.nullable(),
  assignedAgent: UserRefSchema.nullable(),
  /** Former owner when the lead sits in the exit-unassigned queue. */
  unassignedFromAgent: UserRefSchema.nullable().optional(),
  branch: NamedRefSchema.nullable(),
  /** Travel-from city (curated City master ref). */
  departureCity: NamedRefSchema.nullable(),
  /** Travel-to city (curated City master ref). */
  destinationCity: NamedRefSchema.nullable(),
  /** Optional pre-canned Destination master pick (ADR-0041). */
  destination: NamedRefSchema.nullable(),
  budgetAmount: z.number().nullable(),
  budgetCurrency: CurrencyCodeSchema.nullable(),
  expectedTravelStart: z.iso.date().nullable(),
  expectedTravelEnd: z.iso.date().nullable(),
  travellerAdults: z.number().int(),
  travellerChildren: z.number().int(),
  travellerInfants: z.number().int(),
  /// TRUE when `travellerAdults > LARGE_LEAD_ADULTS_THRESHOLD`.
  isLargeLeadQuery: z.boolean().default(false),
  /// 2026-06-25. Per-child ages in declaration order. Length may
  /// differ from `travellerChildren` while the form is being
  /// filled; the server treats them as independent inputs.
  childAges: z.array(z.number().int().min(0).max(120)),
  /// 2026-06-26. Paid-channel ad attribution. All nullable -
  /// organic / direct leads leave them empty.
  adId: z.string().nullable(),
  adName: z.string().nullable(),
  adsetId: z.string().nullable(),
  adsetName: z.string().nullable(),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  nextFollowUpAt: IsoDateTimeSchema.nullable(),
  stageEnteredAt: IsoDateTimeSchema,
  closedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  // ----- Website lead-parsing fields (2026-07-16) -----
  /** Which channel produced the lead (e.g. `website-popup`). */
  querySource: CodedRefSchema.nullable().optional(),
  /** Free-text destination label captured by website forms (survives even
   *  when the form had no destination dropdown). */
  destinationCityText: z.string().nullable().optional(),
  /** Raw enquiry message - website forms embed the package name here
   *  ("Package enquiry: Bali Escape"), which the Lead Parsing System
   *  extracts client-side. */
  notes: z.string().nullable().optional(),
  /** Page the website form was submitted from. */
  querySourceUrl: z.string().nullable().optional(),
});
export type LeadListItem = z.infer<typeof LeadListItemSchema>;

export const LeadListQuerySchema = PaginationQuerySchema.extend({
  /** Trigram fuzzy match across reference, title, customer display name. */
  q: z.string().trim().min(1).max(120).optional(),
  /**
   * Comma-separated UUIDs - restrict the list to exactly these leads (used
   * by report drill-downs). Intersects with the caller's normal visibility
   * scope.
   */
  ids: z.string().trim().max(6000).optional(),
  customerId: UuidSchema.optional(),
  assignedAgentId: UuidSchema.optional(),
  /**
   * Select every lead with NO assigned agent (`assigned_agent_id IS NULL`) -
   * the generic "Unassigned" pool, broader than `exitUnassigned` (which is only
   * the departed-agent release queue). Runs unscoped for the roles allowed to
   * view unassigned (ADMIN / SALES_LEAD), like the exit queue. Powers the
   * Unassigned KPI tile / count.
   */
  assignedAgentIsNull: BooleanQueryParamSchema.optional(),
  /**
   * Restrict to leads that DO have an agent (assigned only). The mirror of
   * `assignedAgentIsNull`. Lets the Daily Tracker's Current / Carry-forward
   * slices count assigned leads only, so the Unassigned slice (no-agent) is
   * disjoint from them and the three sum to the status total with no overlap.
   */
  assignedAgentIsNotNull: BooleanQueryParamSchema.optional(),
  branchId: UuidSchema.optional(),
  queryTypeId: UuidSchema.optional(),
  querySourceId: UuidSchema.optional(),
  /** Restrict to leads for this destination. Powers the Bookings tab
   *  "Destination Wise Query" pivot drill-downs. */
  destinationId: UuidSchema.optional(),
  /** Restrict to leads with NO destination set (`destination_id IS NULL`) -
   *  the pivot's "Unspecified" bucket. */
  destinationUnspecified: BooleanQueryParamSchema.optional(),
  /** Restrict to leads in a travel-date booking-window bucket (the precomputed
   *  `booking_timeline` enum). Powers the Bookings tab "Booking Window (Travel
   *  Timeline)" grid drill-downs. */
  bookingTimeline: BookingTimelineSchema.optional(),
  currentStatusId: UuidSchema.optional(),
  /**
   * Filter to leads currently sitting on a specific QuerySubStatus (e.g.
   * `proposal`, `warm`, `hot` under the `active` parent status). Powers
   * the Leads dashboard's per-sub-status tiles (2026-06-24); independent
   * of `currentStatusId` so an analyst can ask "every lead currently in
   * Hot" without first naming the parent stage.
   */
  currentSubStatusId: UuidSchema.optional(),
  /**
   * Exclude leads currently sitting on a specific status. Used by the
   * tracker's follow-up tiles (legacy parity, ADR-0032 §22) to fence out
   * the "Not Qualified" stage from missed/today's-follow-up counts.
   */
  excludeStatusId: UuidSchema.optional(),
  currentBucket: LeadBucketSchema.optional(),
  priority: LeadPrioritySchema.optional(),
  /** Convenience filter: leads with `next_follow_up_at <= today`. */
  followUpDue: BooleanQueryParamSchema.optional(),
  /**
   * Exact follow-up state (the denormalized `followup_state` column, kept in
   * sync by `recomputeLeadFlags`). `DUE_TODAY` / `OVERDUE` power the Daily
   * Tracker Performance drill-downs, which need the precise state the tracker
   * counted, not the looser `followUpDue` (which conflates due-today with
   * overdue). Composes with `currentBucket=OPEN`.
   */
  followupState: z.enum(['DUE_TODAY', 'OVERDUE']).optional(),
  /**
   * Attempt filter for the Performance drill-downs. `true` = the lead has at
   * least one follow-up logged today (IST); `false` = none today. Mirrors the
   * tracker's "Attempted today" definition so a clicked Attempted / Delta cell
   * lists exactly the leads behind that number.
   */
  attemptedToday: BooleanQueryParamSchema.optional(),
  /**
   * TTV-vs-budget variance bucket, powering the Daily Tracker "TTV to Budget
   * Miss Match" drill-downs. Variance = ((ttvAmount - budgetAmount) /
   * budgetAmount) * 100, computed only for leads with `budgetAmount > 0` and a
   * non-null `ttvAmount` (same predicate the tracker counts). Buckets:
   *   below     -> variance < 0        (TTV under budget)
   *   upTo5     -> 0 <= variance <= 5
   *   upTo10    -> 5 < variance <= 10
   *   moreThan10-> variance > 10
   * Since the comparison is between two columns (arithmetic Prisma `where`
   * can't express), the server resolves the matching lead ids via a raw query
   * and intersects them with the caller's normal visibility scope.
   */
  budgetVarianceBucket: z.enum(['below', 'upTo5', 'upTo10', 'moreThan10']).optional(),
  /**
   * Restrict to leads whose current lost reason is this id. Powers the Lost
   * tab "Lost Reasons" pivot drill-downs. Composes with `currentBucket=LOST`
   * and a `closedAtFrom/To` window ("lost this month").
   */
  lostReasonId: UuidSchema.optional(),
  /**
   * Restrict to leads that have a LOST transition OUT of this active
   * sub-status (proposal / warm / hot) - a `lead_stage_history` row whose
   * `to_status` is a lost status and whose `from_sub_status` has this code.
   * Powers the Lost tab "Lost at X Stage" drill-downs (a stage-history
   * predicate the plain column filters can't express).
   *
   * The sentinel `before_active` is the complement: leads that were lost but
   * NEVER had a LOST transition out of an active sub-status (died at
   * not_qualified, or a legacy row with no stage history). Powers the
   * "Lost before Active (NQ)" column drill. Pair with `currentBucket=LOST`.
   */
  lostFromSubStatusCode: z.enum(['proposal', 'warm', 'hot', 'before_active']).optional(),
  /**
   * Inclusive lower bound on `next_follow_up_at` (YYYY-MM-DD, local). Used
   * by the Pipeline view to count "today's follow-ups" without pulling the
   * full list to the client. Combine with `nextFollowUpTo` for a window;
   * either bound alone works too.
   */
  nextFollowUpFrom: z.iso.date().optional(),
  /** Inclusive upper bound on `next_follow_up_at`. */
  nextFollowUpTo: z.iso.date().optional(),
  /**
   * Restrict to leads that haven't been first-contacted yet
   * (`firstContactedAt IS NULL`). Powers the legacy "Fresh Leads" tile -
   * leads that came in and still need a first touch.
   */
  requiresFirstContact: BooleanQueryParamSchema.optional(),
  /**
   * Pipeline "Revert Pending" tile. Server-side derivation, ORs two
   * legs:
   *   1. `isRevertedToCustomer = TRUE` (operator manually flagged)
   *   2. `currentStatus.code = 'active'`
   *      AND `currentSubStatus.code = 'proposal'`
   *      AND `isItinerarySent = FALSE`
   *
   * Once the Itinerary module starts flipping `isItinerarySent`
   * automatically, leg (2) silently drops out for those leads and
   * the tile only fires on the manual flag.
   */
  revertPending: BooleanQueryParamSchema.optional(),
  /**
   * Performance Insights "Revert pending" drill, which tracks the itinerary-send
   * as the work (not a call): because sending auto-clears `isRevertPending`, the
   * day's cohort is reconstructed from two disjoint legs -
   *   - `pending`        = still unsent (`isRevertPending = TRUE`)  -> the Delta cell
   *   - `reverted_today` = active + proposal + `isItinerarySent = TRUE` with an
   *                        itinerary revision PUBLISHED today       -> the Attempted cell
   *   - `any`            = either of the above                      -> the Lead(s) cell
   */
  revertPendingState: z.enum(['pending', 'reverted_today', 'any']).optional(),
  /**
   * "Rejected approvals" lead-inbox tile. A lead is a rejected-approval
   * when its MOST RECENT `lead_status_change_requests` row has
   * `status = 'REJECTED'` - i.e. the operator's latest approval
   * submission was rejected and hasn't been re-submitted yet.
   * Self-maintaining: a newer PENDING request (re-submit) or an
   * APPROVED latest drops the lead out automatically; a REJECTED
   * latest brings it back. Scoped to the caller's visible leads, same
   * as every other list filter.
   */
  rejectedApproval: BooleanQueryParamSchema.optional(),
  /**
   * Pipeline "Large Lead Query" tile. Server-side derivation backed by
   * the denormalized `is_large_lead_query` column:
   * `travellerAdults > LARGE_LEAD_ADULTS_THRESHOLD` (default 5).
   * Maintained on create/update when adult count changes.
   */
  largeLeadQuery: BooleanQueryParamSchema.optional(),
  /** Convenience filter: leads created in the last N days. Server clamps to [1, 365]. */
  createdInLastDays: z.coerce.number().int().min(1).max(365).optional(),
  /**
   * Inclusive lower bound on `createdAt`. ISO date (YYYY-MM-DD) - treated as
   * 00:00 in the server's local timezone. Takes precedence over
   * `createdInLastDays` when both are supplied.
   */
  createdAtFrom: z.iso.date().optional(),
  /**
   * Inclusive upper bound on `createdAt`. ISO date (YYYY-MM-DD) - treated as
   * 23:59:59.999 in the server's local timezone so a same-day from/to range
   * returns rows created any time that day.
   */
  createdAtTo: z.iso.date().optional(),
  /**
   * Conversion-date window (YYYY-MM-DD, IST day bounds). Filters rows to
   * those whose `closedAt` - the WON/LOST transition instant, with a
   * `stageEnteredAt` fallback for legacy rows that predate `closedAt` -
   * falls in the window. Independent from `createdAtFrom/To`; a caller
   * may combine e.g. `currentBucket=WON` + `closedAtFrom/To` to count
   * "leads converted in this window" (see the All-leads Won KPI). Either
   * bound alone works (gte-only / lte-only); both bounds compose.
   *
   * 2026-07-21. When the query also pins `currentBucket` to a terminal
   * bucket (WON or LOST), the server auto-reroutes any `createdAtFrom/To`
   * window onto these fields (see ADR-0058). Callers that really need
   * the created-cohort semantic on a terminal bucket must set
   * `dateBasis: 'created'` to opt out of the reroute.
   */
  closedAtFrom: z.iso.date().optional(),
  closedAtTo: z.iso.date().optional(),
  /**
   * Explicit control over how the caller's `createdAtFrom/To` window is
   * interpreted for terminal-bucket filters (WON, LOST). Absent = auto:
   * for terminal buckets the window reroutes to `closedAt` (the standard
   * "leads converted / lost in this window" semantic), for every other
   * bucket the window stays on `createdAt`. `'created'` forces the
   * created-cohort semantic (used by the conversion-percent numerator
   * which needs the same createdAt cohort as its denominator).
   * `'closed'` forces the closed-window semantic regardless of bucket
   * (rare - reserved for future analytics use). See ADR-0058.
   */
  dateBasis: z.enum(['created', 'closed']).optional(),
  /**
   * Budget range filters (inclusive). Both bounds are independent so the
   * client can send only-from, only-to, or both. The Lead inbox uses
   * preset brackets ("Under ₹50K", "₹1L - ₹3L", "Above ₹10L") to keep
   * the filter chip a dropdown, but the server-side contract is the raw
   * numeric range so any caller can drive it.
   */
  budgetFrom: z.coerce.number().nonnegative().optional(),
  budgetTo: z.coerce.number().nonnegative().optional(),
  /**
   * Restrict to OPEN-bucket leads whose `currentStatus.code = 'active'` and
   * whose `stageEnteredAt` falls inside this calendar month. Month is
   * expressed as `YYYY-MM` and interpreted in IST (Asia/Kolkata) so the
   * boundary matches the operations team's working day. Composes with the
   * RBAC scope - an agent still only sees their own active leads.
   *
   * Composes with `includeCarryover` to also pull in leads that became
   * active in a prior month and are still OPEN today (see below).
   */
  activeMonth: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'activeMonth must be YYYY-MM')
    .optional(),
  /**
   * When `true` and `activeMonth` is set, also include OPEN-bucket leads
   * whose `currentStatus.code = 'active'` and whose `stageEnteredAt` is
   * before `activeMonth`'s first day - i.e. last month's (and older)
   * still-active leads that the operator should keep working through
   * the current month. Defaults to OFF.
   */
  includeCarryover: BooleanQueryParamSchema.optional(),
  /**
   * Pipeline-list carry-forward. When `true` AND a `createdAtFrom`
   * (or `createdAtTo`) range is set, the `createdAt` predicate is
   * rewritten as:
   *
   *   (createdAt IN [from, to])
   *      OR
   *   (createdAt < from AND currentBucket = 'OPEN'
   *                     AND currentStatus.code = 'active')
   *
   * i.e. the rows the operator would see for that range, plus every
   * still-OPEN + still-`active` lead that pre-dates the window. As a
   * lead remains active it carries forward to each subsequent month
   * automatically; the moment it closes (WON/LOST) or transitions
   * out of `active`, it drops off.
   *
   * Independent of `activeMonth + includeCarryover`, which is the
   * stricter lens documented in ADR-0042. Callers wanting the
   * roll-forward semantic on a generic createdAt-window list use
   * this flag; callers wanting the per-month lens use the other one.
   *
   * The flag is silently ignored when no createdAt range is present
   * (nothing to anchor against) or when the seeded `active` master
   * row is missing on a fresh DB (safer than dropping the filter).
   * Defaults to OFF for backwards compatibility - the Pipeline tab
   * opts in client-side.
   */
  includeActiveCarryover: BooleanQueryParamSchema.optional(),
  /**
   * Optional floor (YYYY-MM-DD, IST day start) on the carry-forward leg
   * of `includeActiveCarryover`. Without it the leg reaches back to the
   * oldest OPEN row in the table - which, post legacy-CRM import, means
   * ~13k pre-2021 leads flooding the grid. The admin client passes the
   * same cutover anchor the Daily Tracker's "Carry fwd" tiles use
   * (`CARRYFORWARD_FLOOR_ISO`), so grid and tile agree on what counts
   * as carry-forward. Ignored when `includeActiveCarryover` is off.
   */
  activeCarryoverFrom: z.iso.date().optional(),
  /**
   * 2026-08-01. Companion to `includeActiveCarryover` for the "Total Leads"
   * view: also fold in leads CLOSED (Won/Lost) inside the createdAt window's
   * period, matched on `closedAt`. So a July lead lost in August still appears
   * in the this-month Total list, matching the tracker/KPI tile (open pool +
   * this-period closures). A separate flag (not merged into
   * `includeActiveCarryover`) so the Active/OPEN-pinned views stay open-only.
   * Ignored when no createdAt range is present.
   */
  includeClosedThisPeriod: BooleanQueryParamSchema.optional(),
  /**
   * 2026-10-01. "Open as of <date>" filter for the report Carry-forward
   * drill-ins: keep leads that were still OPEN at the start of the selected
   * month - `closed_at IS NULL OR closed_at >= openAsOf`. Combined with
   * `createdAtTo < monthStart` this reproduces the report's carried-in cohort
   * EXACTLY (including carry leads that closed later in the month), which the
   * denormalised `currentBucket='OPEN'` carry leg cannot express. YYYY-MM-DD,
   * anchored to the IST day start server-side.
   */
  openAsOf: z.iso.date().optional(),
  /**
   * 2026-08-01. "Total Tasks" DISTINCT count. ORs together the five Daily
   * Tracker task categories so a lead in several (e.g. Not Qualified AND with a
   * missed follow-up) is counted ONCE, not summed. The server builds the union:
   *   created-today  OR  status=not_qualified  OR  (revert-pending, OPEN, not NQ)
   *   OR  (OPEN, follow-up due today)  OR  (OPEN, follow-up overdue).
   * Composes with the caller's createdAt window + owner/website scope (ANDed),
   * so each tracker slice counts its own distinct task pool.
   */
  dailyTaskPool: BooleanQueryParamSchema.optional(),
  /**
   * 2026-10-02. Itinerary-coverage filter (report "Active w/ Itinerary" drill):
   *   - `none`        : the lead has NO itinerary at all
   *   - `in_progress` : has an itinerary but none sent to the customer yet
   *   - `sent`        : has at least one itinerary sent (`isItinerarySent`)
   * Composes with the caller's status/date scope (ANDed).
   */
  itineraryState: z.enum(['none', 'in_progress', 'sent', 'unpublished']).optional(),
  /**
   * Exit-unassigned queue (departed agent's OPEN active / not_qualified
   * leads). ADMIN sees the org-wide queue; SALES_LEAD sees only leads
   * whose former owner was a direct report. Ignores inbox date range.
   */
  exitUnassigned: BooleanQueryParamSchema.optional(),
  /**
   * Narrow the exit-unassigned queue to leads released from ONE former owner.
   * Only read in `exitUnassigned` mode. It narrows within the actor's queue,
   * never widens it: a SALES_LEAD naming a user who is not a direct report
   * gets zero rows.
   */
  unassignedFromAgentId: UuidSchema.optional(),
  /**
   * Count the whole pool including unassigned leads: the Total / Active / NC
   * inbox tiles set this so the exit-released queue (normally hidden from every
   * other list) is folded back in, matching "assigned + unassigned = total".
   * Non-admin callers stay scoped to their own agents regardless, so it only
   * widens the pool for unscoped (ADMIN / OPS) roles.
   */
  includeUnassignedPool: BooleanQueryParamSchema.optional(),
  /**
   * Restrict to leads captured by the public website forms - i.e. leads
   * whose QuerySource.code starts with `website` (`website-popup`,
   * `website-package-page`, `website_callback_form`, ...). Powers the
   * Lead Parsing System screen. Composes with `querySourceId` when the
   * caller pins one specific website source.
   */
  websiteOnly: BooleanQueryParamSchema.optional(),
  /**
   * Hide website-captured leads nobody was given from the CRM inbox /
   * pipeline; those live on the Lead Parsing System screen (`websiteOnly`).
   * A website lead with an agent (round-robin or assigned by hand) is shown
   * like any other. Ignored when `websiteOnly` is also true.
   */
  excludeWebsite: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type LeadListQuery = z.infer<typeof LeadListQuerySchema>;

export const BulkAssignExitUnassignedRequestSchema = z.object({
  ids: z.array(UuidSchema).min(1).max(100),
  assignedAgentId: UuidSchema,
});
export type BulkAssignExitUnassignedRequest = z.infer<typeof BulkAssignExitUnassignedRequestSchema>;

export const BulkAssignExitUnassignedResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
  failed: z.array(
    z.object({
      id: UuidSchema,
      error: z.string(),
    }),
  ),
});
export type BulkAssignExitUnassignedResponse = z.infer<
  typeof BulkAssignExitUnassignedResponseSchema
>;

/**
 * Reassign a selection of leads to one agent (lead detail inbox aside ->
 * Select -> Assign). Each lead takes the same `PATCH /leads/:id` owner change
 * the header picker makes, so every rule stays server-side. 200 = the aside's
 * page-size cap, i.e. everything it can show.
 */
export const BulkReassignLeadsRequestSchema = z.object({
  ids: z.array(UuidSchema).min(1).max(PAGINATION.MAX_PAGE_SIZE),
  assignedAgentId: UuidSchema,
  /** Branch follows the agent, exactly as the single picker sends it. */
  branchId: UuidSchema.nullable().optional(),
});
export type BulkReassignLeadsRequest = z.infer<typeof BulkReassignLeadsRequestSchema>;

export const BulkReassignLeadsResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
  /** Already with that agent - nothing to do. */
  unchanged: z.number().int().nonnegative(),
  failed: z.array(z.object({ id: UuidSchema, error: z.string() })),
});
export type BulkReassignLeadsResponse = z.infer<typeof BulkReassignLeadsResponseSchema>;

/// Priority groups the backlog drip deals the exit-unassigned queue in,
/// first to last. Hot before Warm before Proposal before other Active leads,
/// then Not Qualified leads someone had already reached, then the rest.
export const BACKLOG_PRIORITIES = [
  'HOT',
  'WARM',
  'PROPOSAL',
  'ACTIVE',
  'NQ_CONTACTED',
  'NQ_UNCONTACTED',
] as const;
export const BacklogPrioritySchema = z.enum(BACKLOG_PRIORITIES);
export type BacklogPriority = z.infer<typeof BacklogPrioritySchema>;

/// How the backlog drip is tuned. Lives in app_config under
/// `leads.backlogDrip.*`; admin-only writes.
export const BacklogDripConfigSchema = z.object({
  enabled: z.boolean(),
  /** IST calendar day the queue should be empty by (YYYY-MM-DD). The daily
   *  quota is remaining / working days left until then, recomputed each run. */
  clearByDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Most backlog leads one agent gets per day, whatever the quota says. */
  perAgentDailyCeiling: z.number().int().min(1).max(100),
  /** Send the WhatsApp follow-up template from the new owner on each hand-out. */
  sendFollowupTemplate: z.boolean(),
});
export type BacklogDripConfig = z.infer<typeof BacklogDripConfigSchema>;

export const UpdateBacklogDripConfigRequestSchema = BacklogDripConfigSchema.partial();
export type UpdateBacklogDripConfigRequest = z.infer<typeof UpdateBacklogDripConfigRequestSchema>;

/// The Unassigned page's progress strip: what is left, by priority, what
/// today's quota is and how much of it went out, to whom.
export const BacklogDripSummaryResponseSchema = z.object({
  config: BacklogDripConfigSchema,
  /** IST day the numbers are for. */
  day: z.string(),
  remaining: z.object({
    total: z.number().int().nonnegative(),
    byPriority: z.record(BacklogPrioritySchema, z.number().int().nonnegative()),
    /** Leads whose destination has no expert mapped (or none at all): the
     *  drip skips them, a person has to assign them. Included in `total`. */
    needsManual: z.number().int().nonnegative(),
  }),
  /** Working days (Mon-Sat, IST) left until `clearByDate`, today included. */
  workingDaysLeft: z.number().int().nonnegative(),
  today: z.object({
    quota: z.number().int().nonnegative(),
    assigned: z.number().int().nonnegative(),
    byAgent: z.array(
      z.object({
        agentId: UuidSchema,
        fullName: z.string(),
        assigned: z.number().int().nonnegative(),
        online: z.boolean(),
      }),
    ),
  }),
  /** When the drip last dealt a lead, if ever. */
  lastAssignedAt: z.string().datetime().nullable(),
});
export type BacklogDripSummaryResponse = z.infer<typeof BacklogDripSummaryResponseSchema>;

/// What one manual "Run now" did.
export const BacklogDripRunResponseSchema = z.object({
  assigned: z.number().int().nonnegative(),
  quota: z.number().int().nonnegative(),
  /** Why nothing (or less than the quota) went out. */
  note: z.string().nullable(),
});
export type BacklogDripRunResponse = z.infer<typeof BacklogDripRunResponseSchema>;

/// Preview of how many leads would release into the exit-unassigned queue
/// if the given user exits (deactivate / delete / date-of-exit reached).
/// `total` = activeStage + notQualified; drives the confirmation popup.
export const ExitReleasePreviewSchema = z.object({
  userId: UuidSchema,
  total: z.number().int().nonnegative(),
  breakdown: z.object({
    activeStage: z.number().int().nonnegative(),
    notQualified: z.number().int().nonnegative(),
  }),
});
export type ExitReleasePreview = z.infer<typeof ExitReleasePreviewSchema>;

/// The former owners with leads sitting in the exit-unassigned queue right
/// now, busiest first. Options for the queue's "Former owner" filter - these
/// users are deactivated or deleted, so no agent picker lists them.
export const ExitUnassignedFormerOwnersResponseSchema = z.object({
  data: z.array(
    z.object({
      id: UuidSchema,
      fullName: z.string(),
      leadCount: z.number().int().nonnegative(),
    }),
  ),
});
export type ExitUnassignedFormerOwnersResponse = z.infer<
  typeof ExitUnassignedFormerOwnersResponseSchema
>;

/// List response. `carryoverCount` is only populated when the request
/// carried `activeMonth` AND `includeCarryover=true`: it tells the
/// inbox aside how many of the rows in `data` came from a prior month
/// so the UI can ribbon them and show a "+N carried over" hint
/// without a second round-trip. Absent on every other request shape.
const LeadListMetaSchema = z.object({
  carryoverCount: z.number().int().nonnegative().optional(),
});
export const LeadListResponseSchema = paginatedResponseSchema(LeadListItemSchema).extend({
  meta: LeadListMetaSchema.optional(),
});
export type LeadListResponse = z.infer<typeof LeadListResponseSchema>;

/** One ownership change in a lead's assignment trail. */
export const LeadAssignmentHistoryEntrySchema = z.object({
  fromAgent: UserRefSchema.nullable(),
  toAgent: UserRefSchema.nullable(),
  changedBy: UserRefSchema.nullable(),
  /** 'assign' | 'reassign' | 'unassign' | 'exit_release' | 'backfill'. */
  reason: z.string().nullable(),
  at: z.string(),
});
export type LeadAssignmentHistoryEntry = z.infer<typeof LeadAssignmentHistoryEntrySchema>;

export const LeadDetailSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  reference: z.string(),
  title: z.string(),
  customer: CustomerRefSchema,
  // 2026-06-25 customer snapshot. Denormalized copy of the basic
  // contact fields the operator typed at intake. The `customer`
  // relation above is still authoritative; these are the
  // "what we knew when this lead came in" view, kept in sync on
  // every lead create + update.
  customerFirstName: z.string().nullable(),
  customerLastName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  customerMobileCountry: z.string().nullable(),
  customerMobileNumber: z.string().nullable(),
  queryType: CodedRefSchema,
  querySource: CodedRefSchema.nullable(),
  querySubSource: CodedRefSchema.nullable(),
  queryMedium: CodedRefSchema.nullable(),
  currentStatus: StatusRefSchema,
  currentSubStatus: SubStatusRefSchema.nullable(),
  currentBucket: LeadBucketSchema,
  lostReason: CodedRefSchema.nullable(),
  priority: LeadPrioritySchema.nullable(),
  leadCategory: LeadCategorySchema.nullable(),
  /** Current owner (null = unassigned). */
  assignedAgent: UserRefSchema.nullable(),
  /** The first agent this lead was ever assigned to (immutable); survives
   *  reassignment/unassign so a departed agent stays visible in the history. */
  originalAssignedAgent: UserRefSchema.nullable().optional(),
  /** Ownership-change trail, newest first (assign/reassign/unassign/exit). */
  assignmentHistory: z.array(LeadAssignmentHistoryEntrySchema).optional(),
  branch: NamedRefSchema.nullable(),
  departureCity: NamedRefSchema.nullable(),
  /**
   * Import-captured customer origin city (DB column `lead_departure_city`).
   * Frozen snapshot — shown as a disabled "Customer origin city" on the
   * lead form and itinerary Configure tab. Null for manually created leads.
   */
  leadDepartureCity: NamedRefSchema.nullable(),
  /**
   * Plain-text customer origin captured verbatim from the import sheet
   * (DB column `departure_city_text`, no City-master match). Preferred over
   * `leadDepartureCity` for display; null for manually created leads and
   * imports that predate the plain-text refactor.
   */
  departureCityText: z.string().nullable(),
  destinationCity: NamedRefSchema.nullable(),
  /// Optional pre-canned Destination master pick (ADR-0041).
  destination: NamedRefSchema.nullable(),
  /** Destination master is LONG_HAUL - shows the seller's Screenshots tab. */
  isLongHaul: z.boolean().default(false),
  budgetAmount: z.number().nullable(),
  budgetCurrency: CurrencyCodeSchema.nullable(),
  expectedTravelStart: z.iso.date().nullable(),
  expectedTravelEnd: z.iso.date().nullable(),
  travellerAdults: z.number().int(),
  travellerChildren: z.number().int(),
  travellerInfants: z.number().int(),
  /// 2026-06-25. Per-child ages on the lead row.
  childAges: z.array(z.number().int().min(0).max(120)),
  /// 2026-06-26. Paid-channel ad attribution.
  adId: z.string().nullable(),
  adName: z.string().nullable(),
  adsetId: z.string().nullable(),
  adsetName: z.string().nullable(),
  campaignId: z.string().nullable(),
  campaignName: z.string().nullable(),
  notes: z.string().nullable(),
  // ----- ADR-0035 legacy parity fields -----
  queryDate: z.iso.date().nullable().optional(),
  querySourceUrl: z.string().nullable().optional(),
  ttvAmount: z.number().nullable().optional(),
  ttvCurrency: CurrencyCodeSchema.nullable().optional(),
  /** First-installment / non-refundable deposit collected up front (manual WON). */
  wonDepositAmount: z.number().nullable().optional(),
  preferredHotelCategory: z.number().int().min(1).max(5).nullable().optional(),
  isFirstTimeTraveler: z.boolean().nullable().optional(),
  isHoneymoonCouple: z.boolean().nullable().optional(),
  hasValidPassport: z.boolean().nullable().optional(),
  isDecisionMaker: z.boolean().nullable().optional(),
  bookingTimeline: BookingTimelineSchema.nullable().optional(),
  /** Meta import / intake — whether flights are already booked. */
  isFlightBooked: z.boolean().nullable().optional(),
  /** Meta lead-form id (import-only; not shown on the lead form). */
  formId: z.string().max(64).nullable().optional(),
  /** Meta lead-form name at capture (import-only; not shown on the lead form). */
  formName: z.string().max(500).nullable().optional(),
  /** Free-text preferred travel mode (e.g. "premium"). */
  preferredTravelMode: z.string().trim().max(200).nullable().optional(),
  /** Legacy meal-plan code (ADR-0035). Left in place so archived
   *  rows still display their pre-master value. New writes route
   *  through `mealPlan` (the MealPlan master FK, ADR-0047) and
   *  leave this field untouched. */
  mealOption: MealOptionSchema.nullable().optional(),
  /** MealPlan master reference (ADR-0047). NULL when the operator
   *  hasn't picked one yet or the row pre-dates the FK migration. */
  mealPlan: NamedRefSchema.nullable().optional(),
  expectedPaymentDate: z.iso.date().nullable().optional(),
  requirementSummary: z.string().nullable().optional(),
  // ----- end ADR-0035 -----
  nextFollowUpAt: IsoDateTimeSchema.nullable(),
  stageEnteredAt: IsoDateTimeSchema,
  firstContactedAt: IsoDateTimeSchema.nullable(),
  /// Operator-set bi-state "Is reverted to customer?" flag. Defaults
  /// to FALSE on new leads. See LeadCoreFieldsSchema for semantics.
  isRevertedToCustomer: z.boolean(),
  /// True once the agent has sent the itinerary to the customer.
  /// Defaults to FALSE on new leads. Drives the Revert Pending tile
  /// together with `isRevertedToCustomer` and the (active/proposal)
  /// stage rule.
  isItinerarySent: z.boolean(),
  /// Denormalised: TRUE when adult traveller count exceeds the large-
  /// lead threshold. Drives the Large Lead Query pipeline tile/filter.
  isLargeLeadQuery: z.boolean().default(false),
  closedAt: IsoDateTimeSchema.nullable(),
  /// Win-conversion snapshot - populated once the lead is marked Won.
  saleAmount: z.number().nullable(),
  conversionDate: z.string().nullable(),
  convertedItineraryId: UuidSchema.nullable(),
  convertedRevisionId: UuidSchema.nullable(),
  products: z.array(LeadProductSchema),
  stageHistory: z.array(LeadStageHistoryEntrySchema),
  followUps: z.array(LeadFollowUpSchema),
  /** ADR-0054. The currently PENDING approval request for this lead (LOST
   *  or destination change), if any. The lead detail header / edit form
   *  surfaces a banner + reviewer controls when this is non-null. At most
   *  one PENDING row per lead (partial unique index in the migration). */
  pendingStatusChangeRequest: LeadStatusChangeRequestSchema.nullable(),
  /** The most recent REVIEWED (approved or rejected) status-change request, so
   *  the lead owner always sees the outcome + remarks even after the pending
   *  request resolves - a rejection reason (`reviewerNote` with status REJECTED),
   *  or the reason + note a lost was approved with. Null when none reviewed yet.
   *  Additive + optional so older clients/servers still parse. */
  lastStatusChangeReview: LeadStatusChangeRequestSchema.nullable().optional(),
  /// Times the lead detail form was saved after creation (denormalised).
  detailIterationCount: z.number().int().min(0),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type LeadDetail = z.infer<typeof LeadDetailSchema>;

// ---------------------------------------------------------------------------
// Create / update
// ---------------------------------------------------------------------------

/**
 * Inline-customer payload accepted by lead-create when the operator hasn't
 * pre-picked an existing customer. The server runs an email-OR-phone upsert
 * (`CustomersService.upsertByEmailOrPhoneTx`) and links the resolved
 * customer.id to the new lead in the same transaction.
 *
 * Required fields mirror the legacy "Add Lead" form
 * (`/Users/shubh/Downloads/ClientApp/.../lead-upload.component.ts`):
 *   - `firstName` - the customer's first name (Customer First Name).
 *   - `mobileNumber` - Preferred Contact # (marked red/required in legacy).
 *
 * `email` is optional, matching the legacy form. The server still treats
 * email as a dedup key when supplied, so a returning customer who provides
 * a fresh phone but the same email is correctly matched.
 */
export const NewCustomerInlineSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(80),
  lastName: z.string().trim().max(80).nullable().optional(),
  /** Optional user-facing itinerary name override. */
  primaryName: z.string().trim().max(200).nullable().optional(),
  email: EmailSchema.nullable().optional(),
  mobileCountry: MobileCountrySchema.nullable().optional(),
  mobileNumber: MobileNumberSchema,
  /**
   * Alternate phone number (legacy "Alternate Contact #", ADR-0035).
   * Optional - the customer-upsert dedup only looks at email + mobileNumber.
   */
  altPhone: z.string().trim().max(30).nullable().optional(),
  /**
   * 2026-06-27. Optional alternate email persisted on the customer
   * row. The lead's `customerEmail` intake snapshot stays the
   * value typed into `email` above; this lives only on the
   * customer record.
   */
  alternateEmail: EmailSchema.nullable().optional(),
  /**
   * 2026-06-27. When true AND `alternateEmail` is non-empty, the
   * server creates the customer with:
   *   - customer.email = alternateEmail (becomes the new primary)
   *   - customer.alternateEmail = email (the original primary,
   *     kept on file as the new alternate)
   * The lead's `customerEmail` snapshot is set from the original
   * `email` regardless - the swap is customer-side only so the
   * lead's intake record stays untouched.
   */
  markEmailAsPrimary: z.boolean().optional(),
});
export type NewCustomerInline = z.infer<typeof NewCustomerInlineSchema>;

const LeadCoreFieldsSchema = z.object({
  title: z.string().min(2).max(200),
  /**
   * Existing customer's UUID. Either this OR `customer` (inline fields) must
   * be supplied - the server enforces the choice. Use `customerId` when the
   * operator picked the customer via the search picker; use `customer` for
   * the auto-create path (find-or-create by email/phone).
   */
  customerId: UuidSchema.optional(),
  customer: NewCustomerInlineSchema.optional(),
  // 2026-06-25 customer snapshot. Optional on the wire - the create
  // path derives them from the inline customer payload / linked
  // customer when the caller doesn't send them; PATCH callers send
  // only what changed.
  customerFirstName: z.string().min(1).max(80).nullable().optional(),
  customerLastName: z.string().max(80).nullable().optional(),
  customerEmail: z.string().nullable().optional(),
  customerMobileCountry: z.string().max(8).nullable().optional(),
  customerMobileNumber: z.string().max(32).nullable().optional(),
  /**
   * Query classification. Optional on create - the server defaults to the
   * first active query type when omitted. Source / sub-source / medium /
   * source URL are also optional at intake.
   */
  queryTypeId: UuidSchema.nullable().optional(),
  querySourceId: UuidSchema.nullable().optional(),
  querySubSourceId: UuidSchema.nullable().optional(),
  queryMediumId: UuidSchema.nullable().optional(),
  /** Optional on create -- defaults server-side to the seeded "new" status. */
  initialStatusId: UuidSchema.optional(),
  /** Optional sub-status at create time. Must belong to `initialStatusId`
   *  (or the seeded default status) - server validates. */
  initialSubStatusId: UuidSchema.nullable().optional(),
  // Nullable = blank / unclassified (the importer leaves weak leads blank,
  // never LOW). The create form makes it mandatory for manually-captured
  // leads (like budget), enforced client-side; the API accepts blank so the
  // importer can pass null.
  priority: LeadPrioritySchema.nullable().optional(),
  /**
   * Domestic / International - legacy "Lead Category" dropdown. Optional in
   * the core schema so legacy ETL'd rows (where it may be null) pass through
   * the update path; the create-request override below makes it mandatory
   * for newly captured leads (operator must classify up-front).
   */
  leadCategory: LeadCategorySchema.nullable().optional(),
  /**
   * Lead owner. Server defaults to the signed-in user when omitted or
   * null on create; the form mirrors the same rule client-side.
   */
  assignedAgentId: UuidSchema.nullable().optional(),
  /**
   * "Created By" attribution. Server defaults to the signed-in user when
   * omitted; the lead form lets the operator override it (legacy parity -
   * useful when an admin is capturing a lead on behalf of a colleague).
   */
  createdById: UuidSchema.nullable().optional(),
  branchId: UuidSchema.nullable().optional(),
  /**
   * Travel-from city (curated City master UUID). Nullable here so the partial
   * update schema doesn't require it on every PATCH; create requires it via
   * the override below.
   */
  departureCityId: UuidSchema.nullable().optional(),
  /**
   * Travel-to city (curated City master UUID). Same nullable-here-required-on-
   * create pattern as `departureCityId`.
   */
  destinationCityId: UuidSchema.nullable().optional(),
  /**
   * Optional Destination master pick (ADR-0041). A pre-canned travel
   * target the operator selected at capture time (e.g. "Bali Beach
   * Holiday"). The picker filters out destinations whose country is
   * inactive, so this is server-validated.
   */
  destinationId: UuidSchema.nullable().optional(),
  budgetAmount: z.number().nonnegative().nullable().optional(),
  budgetCurrency: CurrencyCodeSchema.nullable().optional(),
  expectedTravelStart: z.iso.date().nullable().optional(),
  expectedTravelEnd: z.iso.date().nullable().optional(),
  // No `.default()` here on purpose (2026-07 adults-reset bug). Silent
  // defaults hid a form-state miss where Adults/Children typed as 2/2
  // never reached the payload and the API persisted 1/0 instead. The
  // create form still seeds UI defaults via `buildCreateDefaults()`;
  // create/update must send real numbers or fail validation loudly.
  travellerAdults: z.number().int().min(0),
  travellerChildren: z.number().int().min(0),
  travellerInfants: z.number().int().min(0),
  /// 2026-06-25. Per-child ages. Optional in the core schema so
  /// PATCH callers can omit it; create defaults to empty array.
  childAges: z.array(z.number().int().min(0).max(120)).optional(),
  /// 2026-06-26. Paid-channel ad attribution. All optional - leads
  /// captured from organic / direct / referral paths leave them
  /// null. Length-capped so a stray paste of long URLs doesn't
  /// blow up storage.
  adId: z.string().max(64).nullable().optional(),
  adName: z.string().max(500).nullable().optional(),
  adsetId: z.string().max(64).nullable().optional(),
  adsetName: z.string().max(500).nullable().optional(),
  campaignId: z.string().max(64).nullable().optional(),
  campaignName: z.string().max(500).nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  /**
   * Workflow timestamp backing the auto-demote cron (ADR-0038) and the
   * "first-touch" semantic. Normally set by stage transitions when a
   * lead enters the OPEN bucket. Operators don't toggle this directly
   * any more - the human "Is reverted to customer?" flag is now
   * `isRevertedToCustomer` below.
   */
  firstContactedAt: IsoDateTimeSchema.nullable().optional(),
  /**
   * Operator-set bi-state flag for the lead form's "Is reverted to
   * customer?" toggle (ADR-0035 follow-up).
   *   `false` - default for newly captured leads
   *   `true`  - operator manually marked revert pending
   * Distinct from `firstContactedAt` which is the cron's workflow
   * timestamp. Nothing on the server writes this column except the
   * lead PATCH endpoint.
   */
  isRevertedToCustomer: z.boolean().optional(),
  /**
   * True once the agent has sent the itinerary to the customer.
   * Defaults to FALSE on new leads. Operator-editable today; the
   * Itinerary module (Stream B / Phase B4) will flip it automatically
   * once that ships. Drives the derived Revert Pending tile rule:
   * a lead in the `active`/`proposal` stage with `isItinerarySent=false`
   * surfaces as revert-pending even when `isRevertedToCustomer=false`.
   */
  isItinerarySent: z.boolean().optional(),
  // ----- ADR-0035 legacy parity fields -----
  // All optional + nullable so they can be set / cleared individually via
  // PATCH and so an inline-customer create doesn't need to know about them.
  /** When the customer inquired (can differ from `createdAt`). */
  queryDate: z.iso.date().nullable().optional(),
  /** Marketing landing-page URL the lead arrived from. */
  querySourceUrl: z.string().trim().max(500).nullable().optional(),
  /** Tour Total Value the agent quoted (separate from `budgetAmount`). */
  ttvAmount: z.number().nonnegative().nullable().optional(),
  /** Currency for TTV. Defaults to `budgetCurrency` server-side when null. */
  ttvCurrency: CurrencyCodeSchema.nullable().optional(),
  /** 1..5 star hotel preference; NULL = no preference. */
  preferredHotelCategory: z.number().int().min(1).max(5).nullable().optional(),
  /** Three-state flags - null = unknown / not asked. */
  isFirstTimeTraveler: z.boolean().nullable().optional(),
  isHoneymoonCouple: z.boolean().nullable().optional(),
  hasValidPassport: z.boolean().nullable().optional(),
  isDecisionMaker: z.boolean().nullable().optional(),
  bookingTimeline: BookingTimelineSchema.nullable().optional(),
  isFlightBooked: z.boolean().nullable().optional(),
  formId: z.string().max(64).nullable().optional(),
  formName: z.string().max(500).nullable().optional(),
  preferredTravelMode: z.string().trim().max(200).nullable().optional(),
  /** Legacy meal-plan code (ADR-0035). Kept on the write contract
   *  so existing API clients that still send it don't break, but
   *  the new lead form sends `mealPlanId` (ADR-0047) instead and
   *  the service ignores this field when `mealPlanId` is provided. */
  mealOption: MealOptionSchema.nullable().optional(),
  /** MealPlan master FK (ADR-0047). Source of truth for the
   *  operator-picked meal plan on new writes; the dropdown is
   *  driven by `GET /meal-plans?isActive=true`. */
  mealPlanId: UuidSchema.nullable().optional(),
  expectedPaymentDate: z.iso.date().nullable().optional(),
  /** Structured trip summary line. */
  requirementSummary: z.string().trim().max(800).nullable().optional(),
  // ----- end ADR-0035 -----
  products: z.array(LeadProductInputSchema).optional(),
});

/**
 * Create-time requirements:
 *   - `leadCategory`      - Domestic vs International (always required)
 *   - `destinationCityId` - where the customer is going (always required)
 *   - `departureCityId`   - optional on create / Meta import; still
 *     mandatory when moving the lead to Active (active-readiness helper)
 *
 * Import-only snapshots (not on the manual form):
 *   - `leadDepartureCityId` → DB `lead_departure_city` (customer origin)
 *   - `accountId`           → DB `account_id` (Meta Account ID column)
 *   - `metaLeadId`          → DB `meta_lead_id` (Meta Lead ID column)
 *
 * Nullable in `LeadCoreFieldsSchema` so legacy ETL'd rows still pass
 * `UpdateLeadRequestSchema`.
 */
export const CreateLeadRequestSchema = LeadCoreFieldsSchema.extend({
  leadCategory: LeadCategorySchema,
  // Optional on create so Meta import and manual create can omit it.
  // Active transition remains gated by active-readiness.
  departureCityId: UuidSchema.nullable().optional(),
  // Destination city is required on create (legacy parity restored
  // 2026-06-21): every new enquiry must name a destination city so
  // ownership / itinerary / quoting hangs off a real geography ref
  // from day one. PATCH still leaves it nullable so legacy ETL'd
  // rows (the .NET system left these blank for many records) pass
  // `UpdateLeadRequestSchema` without backfill. The lead form's
  // DestinationCityPicker pulls from the same City master as the
  // departure picker with `isActive=true + countryIsActive=true`.
  destinationCityId: UuidSchema,
  /**
   * Import-captured customer origin city (DB `lead_departure_city`).
   * Set by the lead importer from the plain-text Customer origin city
   * column; ignored on subsequent PATCH (snapshot is frozen at create).
   */
  leadDepartureCityId: UuidSchema.nullable().optional(),
  /**
   * Verbatim departure city text from the import sheet (DB
   * `departure_city_text`). The lead importer stores the "Departure City"
   * column exactly as typed - plain text, NO City-master validation or
   * matching - so a customer origin that isn't a curated master row (e.g.
   * "Bangalore") never blocks an import. Optional / manual-create leaves
   * it null.
   */
  departureCityText: z.string().trim().max(200).nullable().optional(),
  /**
   * Meta Account ID from the import sheet. Stored only; not rendered in UI.
   */
  accountId: z.string().trim().max(64).nullable().optional(),
  /**
   * Meta Lead ID from the import sheet. Same treatment as `accountId`:
   * stored verbatim for attribution / dedup bookkeeping; not rendered
   * in the admin UI. Optional / null for manually-created leads.
   */
  metaLeadId: z.string().trim().max(64).nullable().optional(),
})
  .refine((v) => v.travellerAdults + v.travellerChildren + v.travellerInfants > 0, {
    message: 'Lead must have at least one traveller',
    path: ['travellerAdults'],
  })
  .refine(
    (v) =>
      v.expectedTravelStart === null ||
      v.expectedTravelStart === undefined ||
      v.expectedTravelEnd === null ||
      v.expectedTravelEnd === undefined ||
      v.expectedTravelEnd > v.expectedTravelStart,
    {
      message: 'expectedTravelEnd must be after expectedTravelStart',
      path: ['expectedTravelEnd'],
    },
  )
  .refine((v) => Boolean(v.customerId) || Boolean(v.customer), {
    message: 'Pick an existing customer or fill in the customer details',
    path: ['customer'],
  });
export type CreateLeadRequest = z.infer<typeof CreateLeadRequestSchema>;

/**
 * @deprecated Prefer `CreateLeadRequestSchema` — departure city is optional
 * on create. Kept for callers that still want a stricter create shape.
 */
export const CreateLeadFormRequestSchema = CreateLeadRequestSchema.and(
  z.object({ departureCityId: UuidSchema }),
);
export type CreateLeadFormRequest = z.infer<typeof CreateLeadFormRequestSchema>;

// Update: same shape but partial, no defaults applied. `products` here means
// "replace the products list" -- pass undefined to leave them alone.
//
// `querySourceId` is intentionally omitted: the query source is fixed at
// creation time (where the lead came from) and immutable afterwards - no
// role, not even admin, can change it later. If a client sends it on PATCH
// it is silently stripped by the schema; the service also ignores it as a
// belt-and-braces guard.
export const UpdateLeadRequestSchema = LeadCoreFieldsSchema.omit({
  querySourceId: true,
})
  .partial()
  // Same travel-window rule as create: when both ends are supplied on a PATCH,
  // the end date must fall strictly after the start (never the same day).
  .refine(
    (v) =>
      v.expectedTravelStart === null ||
      v.expectedTravelStart === undefined ||
      v.expectedTravelEnd === null ||
      v.expectedTravelEnd === undefined ||
      v.expectedTravelEnd > v.expectedTravelStart,
    {
      message: 'expectedTravelEnd must be after expectedTravelStart',
      path: ['expectedTravelEnd'],
    },
  );
export type UpdateLeadRequest = z.infer<typeof UpdateLeadRequestSchema>;

// ---------------------------------------------------------------------------
// Lead pipeline stats - Phase 2 of the optimization plan
// ---------------------------------------------------------------------------

/**
 * Tile counters returned by `GET /leads/stats`. The keyspace is fixed
 * for the static tiles + dynamic `status:<id>` keys for per-status
 * counts. Consumers should treat the `counts` object as a sparse map:
 * a missing key means zero, not "unknown". See
 * `docs/architecture/leads-pipeline-optimization-plan.md` §Appendix B.
 *
 * `scope` is the canonical scope the response was computed for. For
 * SALES_LEAD this is the literal string `composite` (the response
 * sums multiple `agent:<id>` rows under the hood and there's no
 * single scope name that fits).
 */
export const LeadPipelineStripResponseSchema = z.object({
  scope: z.string(),
  counts: z.record(z.string(), z.number().int().nonnegative()),
  generatedAt: z.string().datetime(),
});
export type LeadPipelineStripResponse = z.infer<typeof LeadPipelineStripResponseSchema>;
